import { useState } from "react";
import { Helmet } from "react-helmet";
import { intl } from "LocaleWrapper";
import { useApolloClient } from "@apollo/client";

import { Card, Filter, PageHeader, Tag, TagSize } from "@cofano/cck";

import SalesCharterStatusChanger from "view/charters/components/status/SalesCharterStatusChanger";
import GroupedTable from "components/customCompenents/table/GroupedTable";
import useGroupedTable from "components/customCompenents/table/useGroupedTable";

import { Language } from "utils";
import { CargoLink, DossierLink, LocationLink, ProductLink, RelationLink, ShipLink } from "utils/LinkUtils";

import ShipSelect, { ShipSelectOption } from "view/ships/components/selects/ShipSelect";
import StatusChanger from "view/statuses/components/changer/StatusChanger";
import { StatusCargoEntity } from "view/statuses/types/status";
import { CargoActionDate } from "view/cargos/components/date/CargoActionDate";
import DocumentProcessingDialog from "view/documents/cargos/components/dialogs/DocumentProcessingDialog";

import { gql } from "__generated__/gql";
import { CallType, DocumentsOverviewQueryQuery, StatusType } from "__generated__/graphql";
import CargoActionTypeTag from "view/cargos/components/tags/CargoActionTypeTag";
import CargoDocumentStatusSelect from "view/documents/cargos/components/selects/CargoDocumentStatusSelect";
import { getCargoDocumentStatusColor } from "view/documents/utils/DocumentUtils";
import { CargoActionDocumentStatus } from "__generated__/enums";
import CargoActionQuantity from "view/cargos/components/quantities/CargoActionQuantity";

type CharterWithCargoActions = DocumentsOverviewQueryQuery["actionsPerCharterPerShip"]["content"][number]["charterWithCargoActions"][number];
type CargoAction = CharterWithCargoActions["cargoActions"][number] & {
	charter: CharterWithCargoActions["charter"];
	allCargos: CharterWithCargoActions["charter"]["cargos"];
};

const CargoDocuments = () => {

	const client = useApolloClient();

	const { tableProps, filterProps, refetch } = useGroupedTable({
		id: "cargoDocuments",
		size: "compact",
		defaultFilters: [ { property: "documentStatuses", value: [ CargoActionDocumentStatus.NewDocumentsCreated ] } ],
		defaultSize: 50,
		onChange: async(pagination, filters) => {
			const result = await client.query({
				query: documentsOverviewQuery,
				variables: {
					pagination: {
						page: pagination.page,
						size: pagination.size,
					},
					search: {
						shipIds: filters.find(it => it.property === "ships")?.value?.map((it: ShipSelectOption) => it.id),
						documentStatuses: filters.find(it => it.property === "documentStatuses")?.value,
					},
				},
			});

			return {
				groups: result.data.actionsPerCharterPerShip.content.map(group => ({
					id: group.ship.id,
					backgroundColor: "#D6E4F1",
					render: () => {
						return (
							<span style={{ display: "flex", alignItems: "center" }}>
								<ShipLink ship={group.ship} />
							</span>
						);
					},
					subGroups: group.charterWithCargoActions.map((charterWithCargoActions) => ({
						id: charterWithCargoActions.charter.id,
						backgroundColor: "#eee",
						render: () => {
							return (
								<div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
									<DossierLink charter={charterWithCargoActions.charter} />
									<SalesCharterStatusChanger
										charter={charterWithCargoActions.charter}
										onCompleted={refetch}
									/>
									<RelationLink relation={charterWithCargoActions.charter.debtor} />
								</div>
							);
						},
						rows: charterWithCargoActions.cargoActions.map((c => ({
							...c,
							charter: charterWithCargoActions.charter,
							allCargos: charterWithCargoActions.charter.cargos,
						}))),
					})),
				})),
				totalElements: result.data.actionsPerCharterPerShip.totalElements,
			};
		},
	});

	const [ cargoProcessingDialog, setCargoProcessingDialog ] = useState<{
		open: boolean,
		action: CargoAction | undefined
	}>({
		open: false,
		action: undefined,
	});

	return <div className="documents">
		<PageHeader>
			<PageHeader.Title>
				{Language("CARGO_DOCUMENTS_TITLE")}
				<Helmet title={intl.formatMessage({ id: "CARGO_DOCUMENTS_TITLE" })} />
			</PageHeader.Title>
		</PageHeader>

		<div style={{ marginBottom: 16 }}>
			<Card disablePadding>
				<Filter
					{...filterProps}
					fields={[ {
						id: "ships",
						title: intl.formatMessage({ id: "SHIPS" }),
						render: (value, onChange) => <ShipSelect value={value} onChange={onChange} multiple />,
					}, {
						id: "documentStatuses",
						title: intl.formatMessage({ id: "ORDER_DOCUMENTS" }),
						render: (value, onChange) => <CargoDocumentStatusSelect value={value} onChange={onChange} multiple />,
						defaultValue: [ CargoActionDocumentStatus.NewDocumentsCreated ],
					}  ]}
				/>
			</Card>
		</div>

		<Card disablePadding>
			<GroupedTable
				{...tableProps}
				getEntityId={row => row.id}
				columns={[
					{
						id: "cargo",
						title: intl.formatMessage({ id: "CARGO" }),
						render: (row) => <CargoLink cargo={row.cargo} />,
					}, {
						id: "status",
						title: intl.formatMessage({ id: "STATUS" }),
						render: (row) => (
							<StatusChanger
								statusType={StatusType.Cargo}
								entity={row.cargo as StatusCargoEntity}
								refetch={refetch}
								showHistory
							/>
						),
					}, {
						id: "type",
						title: intl.formatMessage({ id: "TYPE" }),
						render: (row) => <CargoActionTypeTag type={row.type ?? CallType.Loading} />,
					}, {
						id: "quantity",
						title: intl.formatMessage({ id: "QUANTITY" }),
						render: (row) => (
							<CargoActionQuantity
								cargoAction={row}
								fallbackToActuals
								convertActualsToMton
							/>
						),
					}, {
						id: "product",
						title: intl.formatMessage({ id: "PRODUCT" }),
						render: (row) => <ProductLink product={row.cargo.product} />,
					}, {
						id: "location",
						title: intl.formatMessage({ id: "LOCATION" }),
						render: (row) => <LocationLink location={row.location} />,
					}, {
						id: "ship",
						title: "",
						render: (row) => row.connectingShipPortVisit?.ship?.name,
					}, {
						id: "date",
						title: intl.formatMessage({ id: "DATE" }),
						render: (row) => <CargoActionDate action={row} />,
					}, {
						id: "documents",
						title: intl.formatMessage({ id: "ORDER_DOCUMENTS" }),
						render: (row) => (
							<div
								style={{ cursor: "pointer" }}
								onClick={() => setCargoProcessingDialog({
									open: true,
									action: row,
								})}>
								<Tag
									color={getCargoDocumentStatusColor(row.documentStatus)}
									text={intl.formatMessage({ id: `ACTION_DOCUMENT_STATUS_${row.documentStatus}` })}
									size={TagSize.NORMAL}
								/>
							</div>
						),
					} ]} />
		</Card>

		{cargoProcessingDialog.action && (
			<DocumentProcessingDialog
				open={cargoProcessingDialog.open}
				onClose={() => {
					setCargoProcessingDialog({
						open: false,
						action: undefined,
					});
				}}
				refetch={refetch}
				charter={cargoProcessingDialog.action.charter}
				cargo={cargoProcessingDialog.action.cargo}
				allCargos={cargoProcessingDialog.action.allCargos}
			/>
		)}
	</div>;
};

const documentsOverviewQuery = gql(`#graphql
query documentsOverviewQuery($pagination: Pagination!, $search: DocumentOverviewSearch) {
	actionsPerCharterPerShip(pagination: $pagination, search: $search) {
		content {
			ship {
				id
				name
			}
			charterWithCargoActions {
				charter {
					...SalesCharterStatusChangeSalesCharter

					id
					charterNumber
					type
					debtor {
						id
						name
					}
					broker {
						id
						name
					}
					cargos {
						id
						cargoNumber
						containerNumber
						product {
							id
							name
							description
						}
						actions {
							id
							type
							call {
								id
							}
						}
					}
				}
				cargoActions {
					id
					type
					documentStatus
					documents {
						id
					}
					location {
						id
						description
						locationGroups {
							type
							description
						}
					}
					start
					end
					displayAsWindow
					simulatedTimestamp
					weight
					volume
					measurements {
						skipperWeightUnit
						skipperWeight
						locationWeightUnit
						locationWeight
						skipperVolume
						locationVolume
					}
					connectingShipPortVisit {
						ship {
							id
							name
						}
					}
					cargo {
						id
						cargoNumber
						weight
						volume
						isWeightPrimary
						isVolumePrimary
						waterLevelOptimised
						product {
							id
							name
						}
						lastStatusChange {
							...statusChangeFragment
						}
						statusChanges {
							...statusChangeFragment
						}
						actions {
							id
							type
							call {
								id
							}
						}
					}
				}
			}
		}
		totalElements
	}
}
`);

export default CargoDocuments;
